import { LogIn, LogOut } from "lucide-react";
import { useState } from "react";
import { hasAccount, signOut, useAuth } from "../lib/auth";
import { Sheet } from "./ui/Sheet";
import { SignInPanel } from "./SignInPanel";

/**
 * Account button + sheet. When signed out, the button opens a sign-in
 * sheet with Google. When signed in, the button opens a profile sheet
 * with the user's name/email and a sign-out action.
 *
 * Lives in the top bar — keeps a single entry point for all account
 * actions so we don't sprinkle auth state across the app.
 */
export function AuthMenu() {
  const { status, user } = useAuth();
  const [open, setOpen] = useState(false);

  const signedIn = status === "signed-in" && hasAccount(user) ? user : null;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        aria-label={signedIn ? "Account" : "Sign in"}
        className={
          signedIn
            ? "h-11 min-w-11 px-1.5 inline-flex items-center justify-center rounded-xl hover:bg-ink-100 active:bg-ink-200 transition-colors"
            : "h-11 min-w-10 px-2.5 inline-flex items-center justify-center gap-1.5 rounded-xl text-body-sm font-semibold text-ink-700 hover:bg-ink-100 active:bg-ink-200 transition-colors"
        }
      >
        {signedIn ? (
          <Avatar user={signedIn} size={28} />
        ) : (
          <>
            <LogIn className="size-4" aria-hidden />
            {/* Icon-only on the narrowest phones so Undo/Redo still fit. */}
            <span className="hidden min-[360px]:inline">Sign in</span>
          </>
        )}
      </button>

      <Sheet
        open={open}
        onClose={() => setOpen(false)}
        title={signedIn ? "Account" : "Sign in"}
      >
        {signedIn ? (
          <ProfilePanel onClose={() => setOpen(false)} />
        ) : (
          <SignInPanel onSignedIn={() => setOpen(false)} />
        )}
      </Sheet>
    </>
  );
}

interface AvatarUser {
  displayName: string | null;
  email: string | null;
  photoURL: string | null;
}

export function Avatar({
  user,
  size = 40,
}: {
  user: AvatarUser;
  size?: number;
}) {
  const initials = (user.displayName || user.email || "?")
    .split(/\s+/)
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  if (user.photoURL) {
    return (
      <img
        src={user.photoURL}
        alt={user.displayName ?? user.email ?? "Account"}
        referrerPolicy="no-referrer"
        className="rounded-full object-cover ring-1 ring-ink-200"
        style={{ width: size, height: size }}
      />
    );
  }

  return (
    <div
      className="rounded-full bg-ink-900 text-white flex items-center justify-center font-semibold"
      style={{ width: size, height: size, fontSize: Math.round(size * 0.4) }}
    >
      {initials || "?"}
    </div>
  );
}

function ProfilePanel({ onClose }: { onClose: () => void }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!user) return null;

  async function handleSignOut() {
    setBusy(true);
    setError(null);
    try {
      await signOut();
      onClose();
    } catch (err) {
      setError((err as Error).message || "Couldn't sign out.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-5 pt-2">
      <div className="flex items-center gap-4">
        <Avatar user={user} size={56} />
        <div className="min-w-0">
          <div className="text-base font-semibold text-ink-900 truncate">
            {user.displayName ?? "Signed in"}
          </div>
          {user.email && (
            <div className="text-sm text-ink-500 truncate">{user.email}</div>
          )}
        </div>
      </div>

      <div className="border-t border-ink-100 pt-4">
        <button
          onClick={handleSignOut}
          disabled={busy}
          className="w-full h-11 inline-flex items-center justify-center gap-2 rounded-xl bg-white text-ink-800 ring-1 ring-ink-200 hover:bg-ink-50 active:bg-ink-100 transition-colors font-medium disabled:opacity-50 disabled:pointer-events-none"
        >
          <LogOut className="size-4" />
          {busy ? "Signing out…" : "Sign out"}
        </button>
        {error && (
          <div className="mt-3 text-sm text-rose-600">{error}</div>
        )}
      </div>
    </div>
  );
}
